import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  ColorSubtitleText,
  ColorWarning,
  IgPaper,
  IgTooltip,
  IgTypography,
  IgTypographyTooltip,
} from "@intelligems/ig-theme";
import {
  ExperienceDto,
  ExperienceListDto,
  OrganizationDto,
} from "@intelligems/ig-types";
import { CircularProgress, Grid, Switch } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useConfiguration } from "../../../../api/configurations/use-configuration";
import { getExperienceFeFromDto } from "../../../../api/formatters/from-dto/experiences/experiences";
import { ThemeTestIconFilled } from "../../../../images/Icons/ContentTest/ThemeTest/ThemeTestIconFilled";
import {
  EXPERIMENT_ROUTES,
  PERSONALIZATION_ROUTES,
} from "../../../../routes/routes";
import theme from "../../../../theme";
import { FoundExperimentDetails } from "../../../../types/experience";
import { mpClickPersonalizationOverviewName } from "../../../../utils/mixpanel";
import { getPersonalizationDuration } from "../../utils";
import { MoreMenuPersonalization } from "../MoreMenuPersonalization";

import { PersonalizationOverviewMetrics } from "./PersonalizationOverviewMetrics";
import { PersonalizationOverviewTargeting } from "./PersonalizationOverviewTargeting";
import { TestTypeIconArrayRender } from "./TestTypeIconArrayRender";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        // backgroundColor: theme.palette.mode === "dark" ? "#177ddc" : "#1890ff",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,.35)"
        : "rgba(0,0,0,.25)",
    boxSizing: "border-box",
  },
}));

interface PersonalizationOverviewCardProps {
  personalization: ExperienceDto | ExperienceListDto;
  organization: OrganizationDto;
  isDataLoading: boolean;
  formattedData: {
    visitors: string;
    orders: string;
    revenue: string;
  };
  isUsedInTest: FoundExperimentDetails;
  isSaving: boolean;
  onToggle: () => void;
  subscriptionPlanGate: boolean;
  inactiveSubscriptionGate: boolean;
}
export const PersonalizationOverviewCard = ({
  personalization,
  organization,
  formattedData,
  isDataLoading,
  isUsedInTest,
  isSaving,
  onToggle,
  subscriptionPlanGate,
  inactiveSubscriptionGate,
}: PersonalizationOverviewCardProps) => {
  const navigate = useNavigate();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const shopifyFunctionsEnabled = useConfiguration<boolean>(
    "shopify_functions_enabled"
  );
  const functionSyncError = useMemo(() => {
    return (
      personalization.variations[0].functionSyncError &&
      shopifyFunctionsEnabled?.value
    );
  }, [
    personalization.variations[0].functionSyncError,
    shopifyFunctionsEnabled,
  ]);

  const onNameClick = () => {
    mpClickPersonalizationOverviewName(
      personalization.status === "pending" ? "edit" : "analytics"
    );
    navigate(
      `${
        PERSONALIZATION_ROUTES.Personalization
      }/${personalization.id!}?action=edit&step=${
        personalization.status === "pending" ? 0 : 3
      }`
    );
  };

  const onActivelyTestingClick = () => {
    navigate(
      `${EXPERIMENT_ROUTES.OfferTest}/${isUsedInTest.id}?action=edit&step=0`
    );
  };

  return (
    <Grid item>
      <IgPaper
        sx={{
          borderRadius: "9px",
          padding: "25px 20px",
          ...(functionSyncError && {
            border: `1px solid ${theme.palette.error.main}`,
          }),
        }}
      >
        <Grid
          item
          container
          direction={"row"}
          alignItems={"flex-start"}
          // wrap={"nowrap"}

          sx={{
            height: "90px",
            [theme.breakpoints.down("sm")]: {
              height: "140px",
            },
          }}
        >
          <Grid item xs={12} sm={0.5} mt={1}>
            {isSaving ? (
              <CircularProgress size={15} />
            ) : (
              <IgTooltip
                title={
                  personalization.status !== "started" &&
                  inactiveSubscriptionGate
                    ? "Approve billing to start this personalization."
                    : personalization.status !== "started" &&
                        subscriptionPlanGate
                      ? "This personalization includes modifications not in your plan. Upgrade your plan to start this personalization."
                      : functionSyncError
                        ? "Not enough function space for this personalization"
                        : !isUsedInTest.id
                          ? ""
                          : `This personalization is currently being tested in a ${isUsedInTest.name} test. End the test to turn it on.`
                }
              >
                <AntSwitch
                  size={"small"}
                  disabled={
                    Boolean(isUsedInTest.id) ||
                    isSaving ||
                    inactiveSubscriptionGate ||
                    subscriptionPlanGate
                  }
                  checked={
                    isUsedInTest.id
                      ? false
                      : personalization.status === "started"
                  }
                  onChange={() => {
                    onToggle();
                  }}
                />
              </IgTooltip>
            )}
          </Grid>
          <Grid
            item
            container
            direction={"column"}
            sm={11.5}
            xs={12}
            rowSpacing={1}
          >
            <Grid
              item
              container
              alignItems={"center"}
              direction={"row"}
              justifyContent={"space-between"}
              wrap={"nowrap"}
            >
              <Grid
                item
                container
                alignItems={"center"}
                direction={"row"}
                // wrap={"nowrap"}
                columnSpacing={2}
                xs={5}
              >
                <Grid item onClick={onNameClick}>
                  <IgTypographyTooltip
                    textMaxWidth={"250px"}
                    textWidth={"100%"}
                    textSx={{
                      fontSize: "17px",
                      "&:hover": { cursor: "pointer" },
                    }}
                    text={personalization.name}
                  />
                </Grid>
                <Grid item>
                  <TestTypeIconArrayRender
                    experienceTestTypes={personalization.testTypes}
                  />
                </Grid>
              </Grid>
              <Grid
                item
                xs={8}
                container
                justifyContent={"flex-end"}
                alignItems={"center"}
              >
                <Grid item>
                  <PersonalizationOverviewTargeting
                    audienceState={personalization.audience}
                    personalization={personalization}
                    pageTargeting={personalization.experiencePageTargeting}
                  />
                </Grid>
                <Grid item>
                  <MoreMenuPersonalization
                    key={"more-menu"}
                    isUsedInTest={isUsedInTest}
                    experience={personalization}
                    organization={organization}
                    variations={personalization.variations}
                    renderLocation={"overview"}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item sx={{ minHeight: "32px" }}>
              <IgTypography sx={{ color: ColorSubtitleText }}>
                {personalization?.description}
              </IgTypography>
            </Grid>
            <Grid
              item
              container
              direction={"row"}
              justifyContent={"space-between"}
            >
              <Grid item xs={9} alignItems={"center"} container wrap={"nowrap"}>
                {isUsedInTest.id ? (
                  <IgTooltip
                    title={`This personalization is currently being tested in ${isUsedInTest.name}. Click here to view and manage this test.`}
                  >
                    <Grid
                      item
                      container
                      direction={"row"}
                      sx={{ "&:hover": { cursor: "pointer" } }}
                      onClick={onActivelyTestingClick}
                    >
                      <IgTypography
                        sx={{
                          fontSize: "14px",
                          color: ColorWarning.dark,
                          marginRight: "5px",
                          marginTop: "-1px",
                        }}
                      >
                        Actively testing
                      </IgTypography>
                      <ThemeTestIconFilled
                        width={"14px"}
                        fill={ColorWarning.dark}
                      />
                    </Grid>
                  </IgTooltip>
                ) : (
                  <IgTypography
                    sx={{ fontSize: "14px", color: ColorSubtitleText }}
                  >
                    {getPersonalizationDuration(
                      getExperienceFeFromDto(personalization),
                      storeCurrencyConfig!.value!.locale
                    )}
                  </IgTypography>
                )}
              </Grid>
              {personalization.status === "pending" ? null : (
                <Grid item>
                  <PersonalizationOverviewMetrics
                    formattedData={formattedData}
                    isDataLoading={isDataLoading}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </IgPaper>
    </Grid>
  );
};
